import React from 'react';
import { Box, Chip, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { format, addDays } from 'date-fns';

const today = new Date();
const DEFAULT_START = format(addDays(today, -20), 'dd/MM/yyyy');
const DEFAULT_END   = format(addDays(today,  20), 'dd/MM/yyyy');

const FILTER_DEFAULTS = {
  dateType:          'createdDate',
  reportingStatus:   '',
  status:            '',
  selectedTeamTable: 'all',
} as const;

const DATE_TYPE_LABELS: Record<string, string> = {
  createdDate:  'Created date',
  updatedDate:  'Updated date',
  deliveryDate: 'Delivery date',
};

interface ActiveFiltersBarProps {
  filters: {
    dateType: string;
    reportingStatus: string;
    status: string;
    selectedTeamTable: string;
  };
  dateRange: { startDate: string; endDate: string };
  teamOptions: { uid: string; name: string }[];
  nodeStatus: any[] | null;
  fieldOptions: any[];
  onRemove: (key: string) => void;
  onClearAll: () => void;
  showPriorityOnly: boolean;
}

const ActiveFiltersBar: React.FC<ActiveFiltersBarProps> = ({
  filters,
  dateRange,
  teamOptions,
  nodeStatus,
  showPriorityOnly,
  onRemove,
  onClearAll,
}) => {
  const theme = useTheme();
  const { secondary } = theme.palette;

  const activeChips: { key: string; label: string; value: string }[] = [];
   if (showPriorityOnly) {
    activeChips.push({
      key:   'showPriorityOnly',
      label: 'View',
      value: 'Alerts & Notifications',
    });
  }
  if (filters.dateType !== FILTER_DEFAULTS.dateType) {
    activeChips.push({
      key:   'dateType',
      label: 'Date type',
      value: DATE_TYPE_LABELS[filters.dateType] ?? filters.dateType,
    });
  }

  const rangeIsDefault =
    dateRange.startDate === DEFAULT_START && dateRange.endDate === DEFAULT_END;
  if (!rangeIsDefault) {
    activeChips.push({
      key:   'dateRange',
      label: 'Date range',
      value: `${dateRange.startDate} – ${dateRange.endDate}`,
    });
  }

  if (filters.selectedTeamTable !== FILTER_DEFAULTS.selectedTeamTable) {
    const team = teamOptions.find((t) => t.uid === filters.selectedTeamTable);
    activeChips.push({
      key:   'selectedTeamTable',
      label: 'Process',
      value: team?.name ?? filters.selectedTeamTable,
    });
  }

  if (filters.reportingStatus !== FILTER_DEFAULTS.reportingStatus) {
    const match = Array.isArray(nodeStatus)
      ? nodeStatus.find((s: any) => s._id === filters.reportingStatus)
      : null;
    activeChips.push({
      key:   'reportingStatus',
      label: 'Reporting status',
      value: match?.name ?? filters.reportingStatus,
    });
  }

  if (filters.status !== FILTER_DEFAULTS.status) {
    activeChips.push({
      key:   'status',
      label: 'Status',
      value: filters.status,
    });
  }

  if (activeChips.length === 0) return null;

  return (
    <Box
      sx={{
        display:        'flex',
        alignItems:     'center',
        flexWrap:       'wrap',
        justifyContent: 'flex-end',
        gap:            '6px',
        px:             1.5,
        py:             '6px',
        mb:             1.5,
        borderRadius:   2,
        border:         `1px solid ${alpha(secondary.main, 0.3)}`,
        bgcolor:        alpha(secondary.main, 0.04),
      }}
    >
      {/* Label */}
      <Typography
        sx={{
          fontSize:      '0.65rem',
          fontWeight:    700,
          color:         alpha(secondary.main, 0.5),
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          flexShrink:    0,
        }}
      >
        Active:
      </Typography>

      {/* Chips */}
      {activeChips.map(({ key, label, value }) => (
        <Chip
          key={key}
          size="small"
          onDelete={() => onRemove(key)}
          label={
            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Box
                component="span"
                sx={{ fontSize: '0.65rem', color: secondary.main, fontWeight: 400 }}
              >
                {label}:
              </Box>
              <Box
                component="span"
                sx={{ fontSize: '0.7rem', color: secondary.dark, fontWeight: 600 }}
              >
                {value}
              </Box>
            </Box>
          }
          sx={{
            height:       24,
            bgcolor:      alpha(secondary.main, 0.08),
            border:       `1px solid ${alpha(secondary.main, 0.25)}`,
            borderRadius: '12px',
            '& .MuiChip-label': { px: '8px' },
            '& .MuiChip-deleteIcon': {
              fontSize:   14,
              color:      secondary.main,
              mr:         '5px',
              transition: 'color 0.15s',
              '&:hover':  { color: secondary.dark },
            },
          }}
        />
      ))}

      {/* Divider */}
      <Box sx={{ width: '1px', height: 16, bgcolor: alpha(secondary.main, 0.25), mx: '2px', flexShrink: 0 }} />

      {/* Clear all */}
      <Typography
        component="button"
        onClick={onClearAll}
        sx={{
          fontSize:     '0.7rem',
          fontWeight:   500,
          color:        secondary.main,
          background:   'none',
          border:       'none',
          cursor:       'pointer',
          p:            '2px 6px',
          borderRadius: 1,
          transition:   'color 0.15s, background 0.15s',
          '&:hover':    { color: secondary.dark, bgcolor: alpha(secondary.main, 0.08) },
        }}
      >
        Clear all
      </Typography>
    </Box>
  );
};

export default ActiveFiltersBar;